import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, DatePicker, Input, Select, Space, Table, Tag, Typography } from "antd";
import { ArrowLeftOutlined, ClearOutlined, ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import { useSnackbar } from "notistack";
import API from "../services/api";
import CustomerHistoryDetails from "../components/CustomerHistoryDetails";

const { RangePicker } = DatePicker;

const CustomerHistory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [customer, setCustomer] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [entityType, setEntityType] = useState();
  const [action, setAction] = useState();
  const [dateRange, setDateRange] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        search: search.trim() || undefined,
        entityType,
        action,
        from: dateRange?.[0]?.startOf("day").toISOString(),
        to: dateRange?.[1]?.endOf("day").toISOString(),
      };
      const [historyResponse, customerResponse] = await Promise.all([
        API.get(`/customers/${id}/history`, { params }),
        customer ? Promise.resolve(null) : API.get(`/customers/${id}`),
      ]);
      setHistory(historyResponse.data.data || []);
      setTotal(historyResponse.data.meta?.total || 0);
      if (customerResponse) setCustomer(customerResponse.data.data);
    } catch (error) {
      enqueueSnackbar("Error fetching customer history", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [action, customer, dateRange, enqueueSnackbar, entityType, id, limit, page, search]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);
  useEffect(() => { setPage(1); }, [search, entityType, action, dateRange, limit]);

  const clearFilters = () => {
    setSearch("");
    setEntityType(undefined);
    setAction(undefined);
    setDateRange(null);
  };

  return (
    <Card className="app-page-card" variant="borderless" styles={{ body: { padding: 24 } }}>
      <div className="page-toolbar">
        <div>
          <Space size={10} align="center">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/customers")}>Back</Button>
            <div>
              <Typography.Title level={4} style={{ marginBottom: 4 }}>Customer History</Typography.Title>
              <Typography.Text type="secondary">{customer?.name || "Loading customer..."}</Typography.Text>
            </div>
          </Space>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchHistory} loading={loading}>Refresh</Button>
      </div>

      <Space style={{ width: "100%", marginBottom: 20 }} wrap>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search activity or admin email"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ width: 260 }}
        />
        <Select
          allowClear
          placeholder="All activities"
          value={entityType}
          onChange={setEntityType}
          style={{ width: 170 }}
          options={[
            { value: "customer", label: "Customer" },
            { value: "subscription", label: "Subscription" },
            { value: "customer_config", label: "Customer config" },
            { value: "payment", label: "Payment" },
          ]}
        />
        <Select
          allowClear
          placeholder="All actions"
          value={action}
          onChange={setAction}
          style={{ width: 170 }}
          options={[
            { value: "create", label: "Created" },
            { value: "update", label: "Updated" },
            { value: "soft_delete", label: "Soft deleted" },
            { value: "hard_delete", label: "Permanently deleted" },
            // Payment transitions. A payment needs more than "updated": the
            // audit trail for money has to say which transition happened.
            { value: "payment_link_generated", label: "Payment link generated" },
            { value: "payment_link_resent", label: "Payment link resent" },
            { value: "payment_link_cancelled", label: "Payment link cancelled" },
            { value: "payment_captured", label: "Payment captured" },
            { value: "payment_reconciliation_required", label: "Payment needs reconciliation" },
            { value: "subscription_renewed", label: "Subscription renewed" },
            { value: "receipt_sent", label: "Receipt sent" },
          ]}
        />
        <RangePicker value={dateRange} onChange={setDateRange} />
        <Button icon={<ClearOutlined />} onClick={clearFilters}>Clear filters</Button>
      </Space>

      <Table
        rowKey="id"
        size="medium"
        loading={loading}
        dataSource={history}
        columns={[
          {
            title: "When",
            dataIndex: "occurred_at",
            width: 185,
            render: (value) => value ? new Date(value).toLocaleString() : "N/A",
          },
          {
            title: "Activity",
            width: 180,
            render: (_, entry) => (
              <Space size={4} wrap>
                <Tag
                  color={
                    entry.entity_type === "customer"
                      ? "blue"
                      : entry.entity_type === "subscription"
                        ? "purple"
                        : entry.entity_type === "payment"
                          ? "cyan"
                          : "gold"
                  }
                >
                  {entry.entity_type?.replaceAll("_", " ")}
                </Tag>
                <Tag
                  color={
                    entry.action?.includes("delete") || entry.action?.includes("reconciliation")
                      ? "red"
                      : entry.action === "create" ||
                          entry.action === "payment_captured" ||
                          entry.action === "subscription_renewed"
                        ? "green"
                        : "blue"
                  }
                >
                  {entry.action?.replaceAll("_", " ")}
                </Tag>
              </Space>
            ),
          },
          { title: "Changed by", width: 220, render: (_, entry) => entry.actor_email || entry.actor_id || "System" },
          { title: "Details", render: (_, entry) => <CustomerHistoryDetails entry={entry} /> },
        ]}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50, 100],
          onChange: (nextPage, nextLimit) => { setPage(nextPage); setLimit(nextLimit); },
        }}
        locale={{ emptyText: "No customer history matches these filters." }}
      />
    </Card>
  );
};

export default CustomerHistory;
